import { useEffect, useState } from 'react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErpTable, EmptyRow, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { languageLabel } from '@/shared/lib/language'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, humanise, shortId } from '@/shared/lib/format'
import type {
  SurveyAnswerDto, SurveyFlagDto, SurveyRevealedIdentifiersDto,
} from '@/shared/api/types'
import {
  acceptResponse, getResponse, listFlags, rejectResponse, revealIdentifiers, withdrawResponse,
} from './api'
import { AudioPlayer } from './AudioPlayer'
import { InterviewerPanel } from './InterviewerPanel'
import {
  Pill, count, duration, flagRuleLabel, flagSeverityTone, num, responseStatusLabel,
} from './vocab'

/**
 * One flagged interview, opened from the queue.
 *
 * ## The queue was a list of complaints without this
 *
 * A supervisor asked to accept or reject an interview needs to see what was
 * actually answered, hear the recording where there is one, and read the rule
 * detail that fired. PollSphere's copy tells the user to "review speeders and
 * straightliners in the Collection tab" and there is no such review anywhere in
 * it — the flags were a status the client set on itself.
 *
 * ## Skipped and blank are two different facts
 *
 * `answered: false` with a `skippedByRuleId` means the question was never put:
 * a branch routed past it, and the instrument's skip logic is why. `answered:
 * false` with no rule id means it WAS put and the respondent said nothing. The
 * first is the instrument working; the second is a hole in the interview and may
 * be the interviewer's doing. PollSphere could distinguish them in neither
 * direction — its capture wrote one null for both — so they are rendered apart
 * here and both are labelled, because a reader who has to infer which is which
 * will infer wrong.
 *
 * ## Why the flag detail is safe to show
 *
 * `FlagResponse.detail` carries the fields, thresholds and derived scalars the
 * rule used — `accuracyM`, `impliedKmh`, `interviewsWithinRadius` — and never a
 * respondent's answer. That is what makes it printable next to a masked identity
 * without widening the disclosure, and it is said on screen once because it is
 * the reason the panel exists in this shape.
 */
export function ResponseDrawer({ responseId, onClose, onChanged }: {
  responseId: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const canModerate = useCan('survey:moderate')
  const canReadRaw = useCan('survey:read-raw')
  /**
   * Held in component state and nowhere else.
   *
   * Not on the loaded detail, not in a store, not in the URL — a revealed name
   * that outlived the drawer would be an identifier sitting in memory for every
   * interview opened afterwards, and the audit row says one interview.
   */
  const [revealed, setRevealed] = useState<SurveyRevealedIdentifiersDto | null>(null)
  const [revealing, setRevealing] = useState(false)
  const [revealError, setRevealError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<null | 'reject' | 'withdraw'>(null)
  const [reason, setReason] = useState('')

  /**
   * One interview, one call, one audit row.
   *
   * A failure is shown and nothing else changes: a reveal that could not be made
   * leaves the masks exactly where they were, which is the correct fallback.
   */
  async function doReveal() {
    if (!responseId) return
    setRevealing(true)
    setRevealError(null)
    try {
      setRevealed(await revealIdentifiers(responseId))
    } catch (e) {
      setRevealError(explain(e))
    } finally {
      setRevealing(false)
    }
  }

  const detail = useLoad(
    () => (responseId ? getResponse(responseId) : Promise.resolve(null)), [responseId])
  const flags = useLoad(
    () => (responseId ? listFlags(responseId) : Promise.resolve([])), [responseId])

  useEffect(() => { setPending(null); setReason(''); setError(null) }, [responseId])

  const r = detail.data?.response ?? null
  const answers = detail.data?.answers ?? []

  async function act(run: () => Promise<unknown>, close: boolean) {
    setBusy(true)
    setError(null)
    try {
      await run()
      onChanged()
      if (close) onClose()
      else { detail.reload(); flags.reload(); setPending(null); setReason('') }
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const withdrawn = r?.statusCode === 'WITHDRAWN'

  return (
    <Drawer
      open={!!responseId}
      onClose={onClose}
      width="lg"
      title="Interview"
      subtitle={r ? (
        <span className="flex items-center gap-2">
          <span className="font-mono text-erp-xs">{shortId(r.id)}</span>
          <Pill>{responseStatusLabel(r.statusCode)}</Pill>
          {r.unitName && <Pill>{r.unitName}</Pill>}
          {r.mobilizerName && <Pill>{r.mobilizerName}</Pill>}
        </span>
      ) : undefined}
      footer={r && !withdrawn ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/*
            All three are offered whatever the caller holds. Only `survey:moderate`
            can use them and this client knows that much, so the reason travels in
            visible text beside the buttons rather than in a `title` on a disabled
            control, which no pointer can reach and no screen reader announces.
          */}
          {!canModerate && (
            <span className="text-erp-xs text-muted-foreground mr-auto">
              Deciding an interview needs <span className="font-mono">survey:moderate</span>.
              The buttons are still here; the server will say the same thing.
            </span>
          )}
          <button
            type="button"
            className="erp-btn h-9 text-erp-sm"
            disabled={busy}
            onClick={() => void act(
              () => acceptResponse(r.id, 'Reviewed in the response panel and accepted'), true)}
          >
            Accept
          </button>
          <button
            type="button"
            className="erp-btn h-9 text-erp-sm text-rose-600"
            disabled={busy}
            onClick={() => { setPending(pending === 'reject' ? null : 'reject'); setReason('') }}
          >
            Reject…
          </button>
          <button
            type="button"
            className="erp-btn h-9 text-erp-sm text-rose-600"
            disabled={busy}
            onClick={() => { setPending(pending === 'withdraw' ? null : 'withdraw'); setReason('') }}
          >
            Withdraw consent…
          </button>
        </div>
      ) : undefined}
    >
      <ErrorStrip message={error ?? detail.error} />

      {detail.loading && !r ? <LoadingBlock label="Loading the interview…" /> : r && (
        <>
          {pending && (
            <div className="p-4 border-b border-border bg-muted/20 space-y-2">
              {pending === 'reject' ? (
                <Note tone="bad">
                  A rejection is a decision, not a deletion. The interview STAYS in the dataset
                  with a REJECTED status — an interview that vanished would move the AAPOR
                  denominators in the flattering direction and quietly improve the response
                  rate. The reason goes to the audit log and is what somebody reads when they
                  ask why the numbers changed.
                </Note>
              ) : (
                <Note tone="bad">
                  <strong className="font-semibold">This cannot be undone, and it destroys
                  data.</strong> Withdrawing consent purges the recordings and the respondent
                  identifiers, marks the interview WITHDRAWN and drops it out of every estimate
                  — the answers go too. It is not a rejection: a rejected interview is kept and
                  counted, a withdrawn one is erased because the respondent asked. There is no
                  restore, and the reason you type is the only record of why the figures moved.
                </Note>
              )}
              <textarea
                className="erp-input w-full min-h-[80px] py-2"
                maxLength={500}
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={pending === 'reject'
                  ? 'Why is this interview rejected?'
                  : 'Who asked, when, and how the request reached you.'}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="erp-btn erp-btn-primary h-9 text-erp-sm"
                  disabled={busy}
                  onClick={() => void act(() => (pending === 'reject'
                    ? rejectResponse(r.id, reason.trim())
                    : withdrawResponse(r.id, reason.trim())), pending === 'withdraw')}
                >
                  {busy ? 'Working…'
                    : pending === 'reject' ? 'Reject this interview'
                      : 'Purge and withdraw — permanently'}
                </button>
                <button
                  type="button"
                  className="erp-btn erp-btn-ghost h-9 text-erp-sm"
                  onClick={() => { setPending(null); setReason('') }}
                >
                  Cancel
                </button>
                {/*
                  Both reasons are @NotBlank server-side. The button stays live on
                  an empty box so the server's own sentence about why the reason
                  exists is what a person reads — it is a better answer than a
                  greyed-out control, and it is the only one that is authoritative.
                */}
                <span className="text-erp-xs text-muted-foreground">
                  {reason.trim() === ''
                    ? 'The server refuses this without a written reason. Up to 500 characters.'
                    : `${reason.trim().length} of 500 characters.`}
                </span>
              </div>
            </div>
          )}

          {withdrawn && (
            <div className="p-4">
              <Note tone="bad">
                This interview was withdrawn. The recordings and the respondent identifiers have
                been purged and it is out of every estimate. What is left below is the shell —
                there is nothing further to decide about it.
              </Note>
            </div>
          )}

          <DrawerSection title="Who this is — masked">
            <DrawerField label="Name" value={r.respondentNameMask} mono />
            <DrawerField label="Phone" value={r.respondentPhoneMask} mono />
            <DrawerField label="Gender" value={humanise(r.respondentGender)} />
            <DrawerField label="Age band" value={r.respondentAgeBand
              ? `${humanise(r.respondentAgeBand)}${r.respondentAge === null ? '' : ` · ${r.respondentAge}`}`
              : null} />
            {revealed && (
              <>
                <DrawerField label="Name (revealed)" value={revealed.respondentName} mono />
                <DrawerField label="Phone (revealed)" value={revealed.respondentPhone} mono />
                <DrawerField label="National ID (revealed)"
                  value={revealed.respondentNationalId} mono />
              </>
            )}
            <div className="px-4 py-3 space-y-2">
              {/*
                Said plainly rather than implied by the field name. A masked value
                presented as "Name: G*** W***" invites being read as the name, and
                a moderator who thinks they have the identity will act as if they do.
              */}
              <Note tone="info">
                These are <strong className="font-medium">masks, not values</strong>, composed
                server-side.{' '}
                {canReadRaw
                  ? 'You hold survey:read-raw, so the identifiers can be revealed — one '
                    + 'interview at a time, and each reveal writes an audit row naming you and '
                    + 'this interview.'
                  : 'Revealing them needs the survey:read-raw authority, which you do not hold. '
                    + 'There is no request this screen can make on your behalf that returns the '
                    + 'real name or number.'}
              </Note>
              <ErrorStrip message={revealError} />
              {canReadRaw && !revealed && (
                <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                  disabled={revealing} onClick={() => { void doReveal() }}>
                  {revealing ? 'Revealing…' : 'Reveal identifiers'}
                </button>
              )}
              {revealed && (
                <p className="text-erp-xs text-muted-foreground">
                  Revealed for this interview only. Closing the drawer does not keep them, and
                  the next interview starts masked again.
                </p>
              )}
            </div>
          </DrawerSection>

          <DrawerSection title="The interview">
            <DrawerField label="Status" value={responseStatusLabel(r.statusCode)} />
            <DrawerField label="Disposition" value={humanise(r.dispositionCode)} />
            {/*
              A score, not a percentage. `pct()` would multiply it by a hundred and
              invent a unit the server did not send; nothing on this client turns a
              server scalar into a share.
            */}
            <DrawerField label="Quality score" value={
              <span className="tabular-nums">
                {num(r.qualityScore, 2)}
                <span className="text-muted-foreground text-erp-xs"> · 0–1, computed server-side</span>
              </span>} />
            <DrawerField label="Duration" value={
              <span className="tabular-nums">{duration(r.durationSeconds)}</span>} />
            <DrawerField label="Channel" value={humanise(r.channelCode)} />
            <DrawerField label="Captured" value={r.offline
              ? 'Offline, synced later' : 'Online, live'} />
            <DrawerField label="Consent" value={r.consentGiven
              ? 'Given' : 'Not recorded as given'} />
            <DrawerField label="Where" value={r.unitName} />
            <DrawerField label="Language" value={languageLabel(r.languageCode)} />
            <DrawerField label="Started" value={fmtDateTime(r.startedAt)} />
            <DrawerField label="Submitted" value={
              r.submittedAt ? fmtDateTime(r.submittedAt) : 'Never submitted'} />
          </DrawerSection>

          <DrawerSection
            title="What was answered"
            right={<span className="text-erp-xs text-muted-foreground tabular-nums">
              {count(r.answeredCount)} answered · {count(r.skippedCount)} branched past
              {' · '}{count(r.blankCount)} left blank
            </span>}
          >
            <div className="px-4 py-3 space-y-2">
              <ErpTable head={(
                <>
                  <Th>Question</Th>
                  <Th>Answer</Th>
                  <Th>State</Th>
                </>
              )}>
                {answers.length === 0 ? (
                  <EmptyRow colSpan={3} message={withdrawn
                    ? 'The answers were purged when consent was withdrawn.'
                    : 'This interview carries no answers at all.'} />
                ) : answers.map((a) => <AnswerRow key={a.questionId} answer={a} />)}
              </ErpTable>
              <p className="text-erp-xs text-muted-foreground">
                <strong className="font-medium">Branched past</strong> means the question was
                never put — a skip rule routed around it, and the rule that did it is named.
                <strong className="font-medium"> Left blank</strong> means it WAS put and
                nothing came back. Only the second is a hole in the interview, and only the
                second is something to ask the interviewer about.
              </p>
            </div>
          </DrawerSection>

          <DrawerSection title="Why it is flagged">
            <div className="px-4 py-3 space-y-3">
              <ErrorStrip message={flags.error} />
              {flags.loading && !flags.data ? <LoadingBlock label="Loading the flags…" />
                : (flags.data ?? []).length === 0 ? (
                  <Note tone="info">
                    No rule has fired on this interview. It is here because somebody opened it,
                    not because anything detected a problem.
                  </Note>
                ) : (
                  <>
                    {(flags.data ?? []).map((f) => <FlagCard key={f.id} flag={f} />)}
                    <p className="text-erp-xs text-muted-foreground">
                      The detail on each flag is fields, thresholds and derived scalars —
                      <span className="font-mono"> accuracyM</span>,
                      <span className="font-mono"> impliedKmh</span>,
                      <span className="font-mono"> interviewsWithinRadius</span> — and
                      <strong className="font-medium"> never a respondent's answer</strong>.
                      That is what makes it safe to print here beside a masked identity, and it
                      is why a rule can be argued with: you can see the number it compared
                      against.
                    </p>
                  </>
                )}
            </div>
          </DrawerSection>

          {!withdrawn && (
            <DrawerSection title="The recording">
              <div className="px-4 py-3">
                <AudioPlayer responseId={r.id} />
              </div>
            </DrawerSection>
          )}

          {r.mobilizerId && (
            <DrawerSection title="Who conducted it">
              <div className="px-4 py-3">
                <InterviewerPanel mobilizerId={r.mobilizerId} fallbackName={r.mobilizerName} dense />
              </div>
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  )
}

/** The value the respondent gave, whichever of the five typed columns holds it. */
/**
 * What the respondent said, in the words the option was written in.
 *
 * The labels and the codes are index-aligned, so a label that is missing —
 * an option deleted since — falls back to its code rather than to a gap. The
 * codes used to be all there was, and this table read "LRG-C" in both columns.
 */
function answerValue(a: SurveyAnswerDto): string | null {
  if (a.optionCodes && a.optionCodes.length > 0) {
    const chosen = a.optionCodes.map((code, i) => a.optionLabels?.[i] || code)
    return chosen.join(', ') + (a.otherText ? ` — “${a.otherText}”` : '')
  }
  if (a.number !== null && a.number !== undefined) return String(a.number)
  if (a.bool !== null && a.bool !== undefined) return a.bool ? 'Yes' : 'No'
  if (a.text) return a.text
  if (a.otherText) return `“${a.otherText}”`
  return null
}

function AnswerRow({ answer: a }: { answer: SurveyAnswerDto }) {
  // The whole distinction, in one line: a rule id means the question was never
  // put. Its absence on an unanswered question means it was, and nothing came back.
  const branched = !a.answered && a.skippedByRuleId !== null
  const value = answerValue(a)

  return (
    <tr className={`hover:bg-muted/40 align-top ${branched ? 'text-muted-foreground' : ''}`}>
      <Td>
        {/* The question as it was PUT. The code stays, small and beneath it: it is
            what the codebook, the export column and every crosstab are keyed on,
            so somebody cross-referencing this against an export still needs it. */}
        <span className="block">{a.questionText ?? a.questionCode ?? '—'}</span>
        {a.questionText && a.questionCode && (
          <span className="block font-mono text-erp-xs text-muted-foreground">
            {a.questionCode}
          </span>
        )}
      </Td>
      <Td>
        {a.answered
          ? (value ?? <span className="text-muted-foreground">Answered, no value stored</span>)
          : branched
            ? <span className="italic">Never put to the respondent</span>
            : <span>Put, and nothing came back</span>}
      </Td>
      <Td>
        {a.answered ? <Pill>Answered</Pill>
          : branched ? (
            <span className="inline-flex flex-col gap-0.5">
              <Pill>Branched past</Pill>
              <span className="font-mono text-erp-xs text-muted-foreground">
                rule {shortId(a.skippedByRuleId)}
              </span>
            </span>
          ) : (
            // Not a colour: severity is the one thing that carries colour on this
            // screen, and a blank answer is a fact, not a finding.
            <span className="inline-flex items-center rounded border border-dashed border-border px-1.5 py-0.5 text-erp-xs">
              Shown, left blank
            </span>
          )}
      </Td>
    </tr>
  )
}

/** Anything Jackson can put in a `Map<String,Object>`, rendered without guessing. */
function detailValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

function FlagCard({ flag: f }: { flag: SurveyFlagDto }) {
  const entries = Object.entries(f.detail ?? {})
  const open = f.resolutionCode === 'PENDING'

  return (
    <div className="erp-card p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={flagSeverityTone(f.severityCode)}>{f.severityCode}</Pill>
        <span className="text-erp-sm font-medium">{flagRuleLabel(f.ruleCode)}</span>
        <span className="font-mono text-erp-xs text-muted-foreground">{f.ruleCode}</span>
        <span className="text-erp-xs text-muted-foreground ml-auto">
          raised {fmtDateTime(f.raisedAt)}
        </span>
      </div>

      {entries.length > 0 && (
        <dl className="grid grid-cols-[minmax(120px,40%)_1fr] gap-x-3 gap-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-erp-xs text-muted-foreground truncate">{humanise(k)}</dt>
              <dd className="text-erp-xs font-mono tabular-nums break-words">{detailValue(v)}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="text-erp-xs text-muted-foreground">
        {open
          ? f.severityCode === 'INFO'
            ? 'Open. INFO flags do not suppress the interview from the estimates — this one is '
              + 'a note, not an exclusion.'
            : 'Open, and WARN and HIGH suppress the interview from the estimates until somebody '
              + 'decides. It has not been removed from anything.'
          : `${humanise(f.resolutionCode)}${f.resolvedAt ? ` ${fmtDateTime(f.resolvedAt)}` : ''}`
            + `${f.resolutionNote ? ` — ${f.resolutionNote}` : ''}`}
      </div>
    </div>
  )
}
